/**
 * 上传状态全局管理
 * 用于记录上传失败的文件名等信息
 */

export interface FailedFileInfo {
  fileName: string;
  errorMessage: string;
  timestamp: number;
  fileSize?: number;
  fileType?: string;
}

class UploadStateManager {
  private failedFiles: FailedFileInfo[] = [];
  private listeners: Array<() => void> = [];

  /**
   * 添加失败的文件信息
   */
  addFailedFile(fileName: string, errorMessage: string, file?: File) {
    console.info('addFailedFile', fileName, errorMessage, file);
    const failedFile: FailedFileInfo = {
      fileName: (file as any).webkitRelativePath || fileName,
      errorMessage,
      timestamp: Date.now(),
      fileSize: file?.size,
      fileType: file?.type
    };
    
    this.failedFiles.push(failedFile);
    this.notifyListeners();
    
    console.warn(`文件上传失败: ${fileName}, 错误: ${errorMessage}`);
  }

  /**
   * 获取所有失败的文件
   */
  getFailedFiles(): FailedFileInfo[] {
    return [...this.failedFiles];
  }

  /**
   * 清空失败文件列表
   */
  clearFailedFiles() {
    this.failedFiles = [];
    this.notifyListeners();
  }

  /**
   * 获取失败文件数量
   */
  getFailedCount(): number {
    return this.failedFiles.length;
  }

  /**
   * 订阅状态变化
   */
  subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * 通知所有监听器
   */
  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  /**
   * 移除指定的失败文件记录
   */
  removeFailedFile(fileName: string) {
    this.failedFiles = this.failedFiles.filter(file => file.fileName !== fileName);
    this.notifyListeners();
  }

  /**
   * 检查文件是否在失败列表中
   */
  isFileFailed(fileName: string): boolean {
    return this.failedFiles.some(file => file.fileName === fileName);
  }
}

// 创建全局单例实例
export const uploadStateManager = new UploadStateManager();

// 导出便捷方法
export const addFailedFile = (fileName: string, errorMessage: string, file?: File) => {
  uploadStateManager.addFailedFile(fileName, errorMessage, file);
};

export const getFailedFiles = () => uploadStateManager.getFailedFiles();

export const clearFailedFiles = () => uploadStateManager.clearFailedFiles();

export const getFailedCount = () => uploadStateManager.getFailedCount();

export const subscribeToUploadState = (listener: () => void) => {
  return uploadStateManager.subscribe(listener);
};

export const removeFailedFile = (fileName: string) => {
  uploadStateManager.removeFailedFile(fileName);
};

export const isFileFailed = (fileName: string) => {
  return uploadStateManager.isFileFailed(fileName);
};
